import { useTranslation } from "react-i18next";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta } from "@/lib/seoIndex";
import Breadcrumbs from "@/components/Breadcrumbs";

/**
 * GDPR-compliant Datenschutzerklärung / Privacy Policy.
 *
 * Built around DSGVO Art. 13/14. Lists every concrete data processing
 * we actually do on doing-good.org — no boilerplate-only text. Update
 * this page whenever new tracking or 3rd-party services are added.
 */
export default function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const isDE = i18n.language?.startsWith("de");

  const meta = buildPageMeta("privacy");
  useSEO({
    robots: decideRobots({ pageType: "privacy" }),
    title: meta.title,
    description: meta.description,
    canonical: buildCanonical("/datenschutz"),
  });

  // Data controller — David Schindler (private operator).
  const controller = {
    name: "David Schindler",
    address: "Wacholderweg 3, 93197 Zeitlarn, Deutschland",
    email: "info@doing-good.org",
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-14" data-testid="privacy-page">
      <Breadcrumbs crumbs={[{ label: isDE ? "Datenschutz" : "Privacy" }]} />
      <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">
        {isDE ? "Datenschutzerklärung" : "Privacy policy"}
      </h1>
      <p className="mt-3 text-sm text-[#5F6B61]">
        {isDE
          ? "Stand: " + new Date().toISOString().slice(0, 10)
          : "Last updated: " + new Date().toISOString().slice(0, 10)}
      </p>

      {isDE ? (
        <div className="prose-clay mt-10 space-y-8 text-[#3A463C] leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">1. Verantwortlich für die Datenverarbeitung</h2>
            <p className="mt-3">
              Verantwortlich im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:<br />
              {controller.name}<br />
              {controller.address}<br />
              E-Mail: <a className="underline hover:text-[#5B8C66]" href={`mailto:${controller.email}`}>{controller.email}</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">2. Was wir verarbeiten und warum</h2>
            <h3 className="mt-4 font-bold">2.1 Beim Aufruf der Website (Server-Logs)</h3>
            <p className="mt-2">
              Beim Besuch von doing-good.org werden technisch notwendige Informationen verarbeitet: IP-Adresse, Datum und Uhrzeit, aufgerufene URL, Browser-Typ, Referrer. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an stabilem Betrieb und Sicherheit). Diese Logs werden nach <strong>spätestens 14 Tagen automatisch gelöscht</strong>.
            </p>

            <h3 className="mt-4 font-bold">2.2 Bei Registrierung / Login</h3>
            <p className="mt-2">
              Wir speichern: E-Mail-Adresse, Anzeigename, Passwort (gehasht mit bcrypt), Verifikationsstatus. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) und lit. f (Brute-Force-Schutz). Aufbewahrung: solange das Konto besteht. Löschung: jederzeit auf Anfrage per E-Mail.
            </p>

            <h3 className="mt-4 font-bold">2.3 Cookies</h3>
            <ul className="mt-2 space-y-2 list-disc pl-5">
              <li><strong>access_token / refresh_token (HttpOnly):</strong> Login-Session. Essentiell, nicht einwilligungspflichtig.</li>
              <li><strong>dg_auth_hint:</strong> Hinweis-Cookie, das dem Frontend zeigt, ob ein Nutzer eingeloggt ist, ohne unnötige Server-Anfragen für Crawler. Essentiell.</li>
              <li><strong>i18nextLng (LocalStorage):</strong> gewählte Sprache. Essentiell für die Benutzerfreundlichkeit.</li>
              <li><strong>dg_cookie_consent_v1 (LocalStorage):</strong> Speichert deine Cookie-Einwilligung selbst.</li>
              <li><strong>Analytics:</strong> wenn du im Cookie-Banner zustimmst, laden wir Plausible Analytics (siehe 2.4). Plausible selbst setzt keine Cookies und legt keine persistente Geräte-ID ab.</li>
            </ul>

            <h3 className="mt-4 font-bold">2.4 Plausible Analytics (nur nach Einwilligung)</h3>
            <p className="mt-2">
              Nach deiner ausdrücklichen Einwilligung über das Cookie-Banner setzen wir <a className="underline hover:text-[#5B8C66]" href="https://plausible.io/data-policy" target="_blank" rel="noreferrer noopener">Plausible Analytics</a> (Plausible Insights OÜ, Estland) ein, um aggregierte Nutzungsstatistiken zu verstehen: Seitenaufrufe, ungefähre Referrer-Kategorie, Browser und Land auf Länder-Ebene. Plausible ist <strong>cookielos</strong>, verwendet keine persistenten Geräte-IDs und erstellt <strong>keine Nutzerprofile</strong>. IP-Adressen werden ausschließlich serverseitig und transient zur Sessionsalt-Berechnung verarbeitet und nicht gespeichert. Zusätzlich erfassen wir wenige aggregierte Ereignisse ohne personenbezogene Daten: Klick auf eine NGO-Verlinkung (nur Domain, keine vollständige URL), Sprachwechsel und Speichern einer Gelegenheit. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Du kannst deine Einwilligung jederzeit über das Cookie-Banner widerrufen.
            </p>

            <h3 className="mt-4 font-bold">2.5 E-Mail-Versand (Resend)</h3>
            <p className="mt-2">
              Für transaktionale E-Mails (Konto-Verifikation, Passwort-Reset) nutzen wir Resend Inc. (San Francisco, USA). E-Mail-Adresse, Name und Verifikationslink werden an Resend übertragen. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO. Datenübermittlung in die USA erfolgt auf Basis der EU-Standardvertragsklauseln. Mehr: <a className="underline hover:text-[#5B8C66]" href="https://resend.com/legal/privacy-policy" target="_blank" rel="noreferrer noopener">resend.com/legal/privacy-policy</a>.
            </p>

            <h3 className="mt-4 font-bold">2.6 Externe Links</h3>
            <p className="mt-2">
              doing-good.org verlinkt direkt auf die Websites gemeinnütziger Organisationen (z.B. msf.org, betterplace.org, vostel.de). Sobald du auf einen solchen Link klickst, verlässt du unsere Website. Die Datenschutzpraktiken der Zielwebsite liegen in deren alleiniger Verantwortung.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">3. Wir verkaufen oder vermieten keine Daten</h2>
            <p className="mt-3">
              Wir geben deine personenbezogenen Daten ausschließlich an Auftragsverarbeiter weiter (Resend für E-Mail, Hosting-Provider Emergent), niemals zu Werbe- oder Trackingzwecken.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">4. Deine Rechte nach DSGVO</h2>
            <p className="mt-3">Du hast jederzeit das Recht auf:</p>
            <ul className="mt-3 space-y-1 list-disc pl-5">
              <li>Auskunft (Art. 15) — was wir über dich gespeichert haben</li>
              <li>Berichtigung (Art. 16) — falscher Daten</li>
              <li>Löschung (Art. 17) — „Recht auf Vergessenwerden“</li>
              <li>Einschränkung der Verarbeitung (Art. 18)</li>
              <li>Datenübertragbarkeit (Art. 20) — Export deiner Daten</li>
              <li>Widerspruch gegen Verarbeitung (Art. 21)</li>
              <li>Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft</li>
              <li>Beschwerde bei einer Aufsichtsbehörde (Art. 77) — z.B. dem zuständigen Datenschutzbeauftragten deines Bundeslandes</li>
            </ul>
            <p className="mt-3">
              Alle Rechte machst du formlos geltend per E-Mail an <a className="underline hover:text-[#5B8C66]" href={`mailto:${controller.email}`}>{controller.email}</a>. Wir antworten innerhalb von 30 Tagen.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">5. Sicherheit</h2>
            <p className="mt-3">
              Passwörter werden ausschließlich als bcrypt-Hash gespeichert. Wiederholte fehlgeschlagene Login-Versuche werden für 15 Minuten gesperrt (Brute-Force-Schutz). Die Verbindung zu doing-good.org ist durchgängig HTTPS/TLS-verschlüsselt.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">6. Änderungen dieser Erklärung</h2>
            <p className="mt-3">
              Wir aktualisieren diese Datenschutzerklärung, wenn sich technische Gegebenheiten oder rechtliche Anforderungen ändern. Das Aktualisierungsdatum oben gibt den letzten Stand wieder.
            </p>
          </section>
        </div>
      ) : (
        <div className="prose-clay mt-10 space-y-8 text-[#3A463C] leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">1. Data controller</h2>
            <p className="mt-3">
              Controller per Art. 4(7) GDPR:<br />
              {controller.name}<br />
              {controller.address}<br />
              Email: <a className="underline hover:text-[#5B8C66]" href={`mailto:${controller.email}`}>{controller.email}</a>
            </p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">2. What we process and why</h2>
            <p className="mt-3"><strong>Server logs:</strong> IP, timestamp, URL, browser, referrer. Auto-deleted after 14 days. Legal basis: Art. 6(1)(f) GDPR.</p>
            <p className="mt-3"><strong>Account data:</strong> email, display name, bcrypt password hash, verification status. Kept while your account exists. Legal basis: Art. 6(1)(b).</p>
            <p className="mt-3"><strong>Cookies:</strong> only essential cookies (auth + language) by default. Optional analytics are loaded <em>only</em> after explicit consent via the cookie banner.</p>
            <p className="mt-3">
              <strong>Plausible Analytics (consent-only):</strong> if you accept analytics in the cookie banner, we load <a className="underline hover:text-[#5B8C66]" href="https://plausible.io/data-policy" target="_blank" rel="noreferrer noopener">Plausible Analytics</a> (Plausible Insights OÜ, Estonia). Plausible is <strong>cookieless</strong>, uses no persistent device IDs and builds <strong>no user profiles</strong>. It reports aggregate page views, approximate referrer category, browser and country-level location. IPs are processed only transiently server-side to derive a daily session salt and are not stored. We additionally record a small number of aggregate, non-personal events: outbound NGO link click (domain only, never the full URL), language switch, and save-opportunity click. Legal basis: Art. 6(1)(a) GDPR (consent). You can withdraw consent at any time via the cookie banner.
            </p>
            <p className="mt-3"><strong>Email delivery (Resend Inc., USA):</strong> we send verification + password-reset emails via Resend. Email transferred under EU Standard Contractual Clauses.</p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">3. We never sell or rent your data</h2>
            <p className="mt-3">Personal data is only shared with processors (Resend for email, our hosting provider) — never for ads or third-party tracking.</p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">4. Your GDPR rights</h2>
            <p className="mt-3">Access, rectification, erasure, restriction, portability, objection, withdrawal of consent, complaint to a supervisory authority. Email us at <a className="underline hover:text-[#5B8C66]" href={`mailto:${controller.email}`}>{controller.email}</a>; we respond within 30 days.</p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">5. Security</h2>
            <p className="mt-3">Passwords stored as bcrypt hashes. 15-minute lockout after repeated failed logins. End-to-end HTTPS/TLS.</p>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#1F2A24]">6. Changes</h2>
            <p className="mt-3">We update this policy when our technical or legal situation changes. The &ldquo;last updated&rdquo; date above reflects the latest version.</p>
          </section>
        </div>
      )}
    </div>
  );
}
